/*
 * dragDrop.ts — bind drag-and-drop file loading to any DOM element.
 *
 * Phase 10. The inbound-paths story: paste, drag, share-URL, recents. This
 * module covers drag — any .md/.markdown/.mdown/.txt file (or text/* mime)
 * dropped on the bound target is read and handed off to `onLoad`.
 *
 * P1.9 from the 2026-05-23 codex audit: cap drops at 5MB. A 200MB log file
 * dropped accidentally would lock the tab during file.text() + the
 * subsequent markdown render. The optional onError callback surfaces the
 * reject reason — App.svelte wires it to a brief alert.
 *
 * Wired against `document.body` in App.svelte so the user can drop
 * anywhere on the window — the preview pane is the obvious target, but the
 * editor pane and even the chrome should accept too. No drop-zone overlay;
 * the drag-over class flashes a dashed outline on the body for feedback.
 *
 * Returns a teardown function so callers (typically onMount) can clean up.
 */

// Markdown source above this is pathological — a 5MB markdown doc would
// already lock the editor + preview during render. Anything bigger is
// surely the wrong file (logs, sql dumps, ndjson).
export const MAX_DROP_BYTES = 5 * 1024 * 1024;

export function isDropAcceptable(file: File): { ok: true } | { ok: false; reason: string } {
  const isMarkdownExt = /\.(md|markdown|mdown|txt)$/i.test(file.name);
  const isTextMime = file.type.startsWith('text/');
  if (!isMarkdownExt && !isTextMime) {
    return { ok: false, reason: 'Not a markdown or text file.' };
  }
  if (file.size > MAX_DROP_BYTES) {
    return {
      ok: false,
      reason: `File too large (${Math.round(file.size / (1024 * 1024))}MB). Max 5MB.`,
    };
  }
  return { ok: true };
}

export function bindDragDrop(
  target: HTMLElement,
  onLoad: (text: string) => void,
  onError?: (msg: string) => void,
): () => void {
  function onDragOver(e: DragEvent): void {
    e.preventDefault();
    target.classList.add('drag-over');
  }

  function onDragLeave(): void {
    target.classList.remove('drag-over');
  }

  async function onDrop(e: DragEvent): Promise<void> {
    e.preventDefault();
    target.classList.remove('drag-over');
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    const verdict = isDropAcceptable(file);
    if (!verdict.ok) {
      // Wrong-file-type drops stay silent (the original behavior); only
      // size errors fire onError, since a 200MB drop is almost always a
      // genuine attempt where the user needs to know why nothing loaded.
      if (/too large/i.test(verdict.reason) && onError) onError(verdict.reason);
      return;
    }
    try {
      const text = await file.text();
      onLoad(text);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Failed to read file');
    }
  }

  target.addEventListener('dragover', onDragOver);
  target.addEventListener('dragleave', onDragLeave);
  target.addEventListener('drop', onDrop);

  return () => {
    target.removeEventListener('dragover', onDragOver);
    target.removeEventListener('dragleave', onDragLeave);
    target.removeEventListener('drop', onDrop);
  };
}
